import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import debounce from "lodash/debounce";
import { FaSortAlphaDown } from "react-icons/fa";

import { Button } from "components/button";
import OverviewData from "./types/OverviewData";
import InvoiceCard from "./components/InvoiceCard";
import CustomerCard from "./components/CustomerCard";
import PaymentCard from "./components/PaymentCard";
import Customer from "./types/Customer";
import Invoice from "./types/Invoice";

import { useTranslation } from "react-i18next";
import ClassMappingModal from "./components/ClassModal";
import AccountMappingModal from "./components/AccountMappingModal";
import OptionsModal from "./components/OptionsModal";

export type QboClass = {
  id: number;
  fk_business_id: number;
  fk_division_id: number | null;
  qbo_class_id: string;
  name: string;
  active: boolean;
  created_at: string;
  updated_at: string;
};

export type Division = {
  id: number;
  fk_business_id: number;
  label: string;
  price_type: string;
  production: string | null;
  show_production: boolean;
  qbo_class_id?: string | null; // ← joined from qbo_class if mapped
};

type Props = {};

type MessageFilter = null | "has" | "error" | "none";

type CustomerFilter = {
  search: string;
  hasMessage: MessageFilter;
  hasQboId: boolean | null;
  sortColumn: string;
  sortDirection: string;
};

type InvoiceFilter = {
  search: string;
  hasMessage: MessageFilter;
  hasQboId: boolean | null;
  sortColumn: string;
  sortDirection: string;
};

type PaymentFilter = {
  search: string;
  hasMessage: MessageFilter;
  hasQboId: boolean | null;
  sortColumn: string;
  sortDirection: string;
};

const customerSortOptions = [
  { label: "Creation Date", value: "creation_date" },
  { label: "First Name", value: "first_name" },
  { label: "Last Name", value: "last_name" },
  { label: "Company Name", value: "company_name" },
];

const invoiceSortOptions = [
  { label: "Creation Date", value: "creation_date" },
  { label: "Customer First Name", value: "first_name" },
  { label: "Customer Last Name", value: "last_name" },
  { label: "Company Name", value: "company_name" },
  { label: "Total Amount", value: "total_amount" },
];

const paymentSortOptions = [
  { label: "Creation Date", value: "creation_date" },
  { label: "Customer First Name", value: "first_name" },
  { label: "Customer Last Name", value: "last_name" },
  { label: "Company Name", value: "company_name" },
  { label: "Total Amount", value: "amount" },
];

const authenticate = async () => {
  window.location.href = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/oauth/start`;
};

const QuickbooksOnline = (props: Props) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const tBase = "views.admin.quickbooks.online.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [showClassMappingModal, setShowClassMappingModal] = useState(false);
  const [showAccountMappingModal, setShowAccountMappingModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);

  const [limit, setLimit] = useState<number>(5);
  
  const [customerFilter, setCustomerFilter] = useState<CustomerFilter>({
    search: "",
    hasMessage: null,
    hasQboId: null,
    sortColumn: "ID",
    sortDirection: "DESC",
  });
  const [invoiceFilter, setInvoiceFilter] = useState<InvoiceFilter>({
    search: "",
    hasMessage: null,
    hasQboId: null,
    sortColumn: "ID",
    sortDirection: "DESC",
  });
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>({
    search: "",
    hasMessage: null,
    hasQboId: null,
    sortColumn: "ID",
    sortDirection: "DESC",
  });

  const [showCustomerSortPopup, setShowCustomerSortPopup] = useState(false);
  const [showInvoiceSortPopup, setShowInvoiceSortPopup] = useState(false);
  const [showPaymentSortPopup, setShowPaymentSortPopup] = useState(false);

  const fetchOverview = async () => {
    const response = await axios.get<OverviewData>(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/overview`,
      {
        params: {
          customerFilter: JSON.stringify(customerFilter),
          invoiceFilter: JSON.stringify(invoiceFilter),
          paymentFilter: JSON.stringify(paymentFilter),
          limit: limit,
        },
      }
    );
    return response.data;
  };

  const refreshToken = async () => {
    await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/oauth/refresh`
    );
    invalidateQueries();
  };

  const { data, isError, error } = useQuery(
    ["quickbooks-online", customerFilter, invoiceFilter, paymentFilter, limit],
    fetchOverview
  );

  const debouncedCustomerSearch = useCallback(
    debounce((value: string) => {
      setCustomerFilter((prevFilter) => ({ ...prevFilter, search: value }));
    }, 300),
    []
  );

  const debouncedInvoiceSearch = useCallback(
    debounce((value: string) => {
      setInvoiceFilter((prevFilter) => ({ ...prevFilter, search: value }));
    }, 300),
    []
  );

  const debouncedPaymentSearch = useCallback(
    debounce((value: string) => {
      setPaymentFilter((prevFilter) => ({ ...prevFilter, search: value }));
    }, 300),
    []
  );

  const handleCustomerSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    debouncedCustomerSearch(e.target.value);
  };

  const handleInvoiceSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    debouncedInvoiceSearch(e.target.value);
  };

  const handlePaymentSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    debouncedPaymentSearch(e.target.value);
  };

  const toggleFilterValue = (currentValue: boolean | null) => {
    if (currentValue === null) return true;
    if (currentValue === true) return false;
    return null;
  };

  const toggleMessageFilter = (currentValue: MessageFilter): MessageFilter => {
    if (currentValue === null) return "has";
    if (currentValue === "has") return "error";
    if (currentValue === "error") return "none";
    return null;
  };

  const messageFilterLabel = (value: MessageFilter) => {
    if (value === null) return tr("Any Message");
    if (value === "has") return tr("Has Message");
    if (value === "error") return tr("Error Message");
    return tr("No Message");
  };

  const messageFilterClass = (value: MessageFilter) => {
    if (value === null) return "bg-gray-300";
    if (value === "has") return "bg-green-300";
    if (value === "error") return "bg-red-300";
    return "bg-gray-300";
  };

  const handleCustomerFilterChange = (filter: "hasMessage" | "hasQboId") => {
    setCustomerFilter((prevFilter) => {
      if (filter === "hasMessage") {
        return {
          ...prevFilter,
          hasMessage: toggleMessageFilter(prevFilter.hasMessage),
        };
      }
      return {
        ...prevFilter,
        hasQboId: toggleFilterValue(prevFilter.hasQboId),
      };
    });
  };

  const handleInvoiceFilterChange = (filter: "hasMessage" | "hasQboId") => {
    setInvoiceFilter((prevFilter) => {
      if (filter === "hasMessage") {
        return {
          ...prevFilter,
          hasMessage: toggleMessageFilter(prevFilter.hasMessage),
        };
      }
      return {
        ...prevFilter,
        hasQboId: toggleFilterValue(prevFilter.hasQboId),
      };
    });
  };

  const handlePaymentFilterChange = (filter: "hasMessage" | "hasQboId") => {
    setPaymentFilter((prevFilter) => {
      if (filter === "hasMessage") {
        return {
          ...prevFilter,
          hasMessage: toggleMessageFilter(prevFilter.hasMessage),
        };
      }
      return {
        ...prevFilter,
        hasQboId: toggleFilterValue(prevFilter.hasQboId),
      };
    });
  };

  const handleSortCustomerChange = (column: string, direction: string) => {
    setCustomerFilter((prevFilter) => ({
      ...prevFilter,
      sortColumn: column,
      sortDirection: direction,
    }));
  };

  const handleSortInvoiceChange = (column: string, direction: string) => {
    setInvoiceFilter((prevFilter) => ({
      ...prevFilter,
      sortColumn: column,
      sortDirection: direction,
    }));
  };

  const handleSortPaymentChange = (column: string, direction: string) => {
    setPaymentFilter((prevFilter) => ({
      ...prevFilter,
      sortColumn: column,
      sortDirection: direction,
    }));
  };

  const customers = useMemo(() => data?.customers || [], [data]);
  const invoices = useMemo(() => data?.invoices || [], [data]);
  const payments = useMemo(() => data?.payments || [], [data]);

  const connectionStatus = useMemo(() => {
    if (!data || !data.connectionDetails) return null;
    const { access_token_expiry, expires_in, refresh_token_expiry } =
      data.connectionDetails;
    const expiryDate = new Date(
      new Date(access_token_expiry).getTime() + expires_in * 1000
    );
    const isExpired = new Date() > expiryDate;

    return {
      isExpired,
      message: isExpired
        ? tr("Connection to QuickBooks has expired. Please reconnect.")
        : tr("Connected to QuickBooks."),
      expiryDate: expiryDate.toLocaleString(),
      access_token_expiry: new Date(access_token_expiry).toLocaleString(),
      refresh_token_expiry: new Date(refresh_token_expiry).toLocaleString(),
    };
  }, [data, tr]);

  const connectButtonLabel = useMemo(() => {
    const hasConnection = Boolean(data?.connectionDetails);
    if (!hasConnection) return tr("Connect to QuickBooks Online");
    if (connectionStatus?.isExpired) return tr("Reconnect to QuickBooks Online");
    return tr("Connected to QuickBooks Online");
  }, [connectionStatus?.isExpired, data?.connectionDetails, tr]);

  const connectButtonIsPrimary = useMemo(() => {
    const hasConnection = Boolean(data?.connectionDetails);
    return !hasConnection || Boolean(connectionStatus?.isExpired);
  }, [connectionStatus?.isExpired, data?.connectionDetails]);

  const connectButtonDisabled = useMemo(() => {
    const hasConnection = Boolean(data?.connectionDetails);
    // If we have a valid connection, don't invite users to "do something" by clicking OAuth again.
    return hasConnection && !Boolean(connectionStatus?.isExpired);
  }, [connectionStatus?.isExpired, data?.connectionDetails]);

  const connectButtonStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (!connectButtonDisabled) return undefined;
    // Inline style overrides the shared Button disabled styling for this one case.
    return {
      background: "#16a34a",
      color: "#ffffff",
      opacity: 1
    };
  }, [connectButtonDisabled]);

  const invalidateQueries = async () => {
    await queryClient.invalidateQueries("quickbooks-online");
  };

  return (
    <div className="text-sm h-[calc(100vh-184px)] flex flex-col">
      <div className="flex flex-row items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <h3>Quickbooks Online</h3>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Show:</label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
            <span className="text-sm text-gray-600">per column</span>
          </div>
        </div>
        <div>
          <Button className="ml-2" onClick={() => setShowOptionsModal(true)}>
            Options
          </Button>

          <Button className="ml-2" onClick={() => setShowAccountMappingModal(true)}>
            Accounts
          </Button>

          <Button className="ml-2" onClick={() => setShowClassMappingModal(true)}>
            Classes
          </Button>

          <Button className="ml-2" onClick={refreshToken}>{tr("Refresh Token")}</Button>
          <Button
            className="ml-2"
            onClick={authenticate}
            primary={connectButtonIsPrimary}
            disabled={connectButtonDisabled}
            style={connectButtonStyle}
          >
            {connectButtonLabel}
          </Button>
        </div>
      </div>
      <div className="border rounded bg-secondary bg-opacity-10 h-14 p-1">
        {/* {isLoading && <div>{tr("Loading")}...</div>} */}
        {isError && (
          <div>
            Error: {error ? (error as Error).message : "Failed to fetch data"}
          </div>
        )}
        {connectionStatus && (
          <div>
            <p>Access Token Expires: {connectionStatus?.access_token_expiry}</p>
            <p>
              Refresh Token Expires: {connectionStatus?.refresh_token_expiry}
            </p>
          </div>
        )}
      </div>
      <div className="h-4" />
      <div className="flex flex-row flex-grow overflow-hidden">
        <div className="border border-black rounded-md p-2 bg-slate-200 mb-2 pt-2 w-[32%] mr-1 flex flex-col">
          <div className="flex flex-row justify-between items-center mb-2">
            <h4 className="mr-2">{tr("Customers")}</h4>
            <div className="flex flex-row items-center">
              <button
                onClick={() => handleCustomerFilterChange("hasMessage")}
                className={`p-1 rounded text-[11px] mr-2 ${messageFilterClass(customerFilter.hasMessage)}`}
              >
                {messageFilterLabel(customerFilter.hasMessage)}
              </button>
              <button
                onClick={() => handleCustomerFilterChange("hasQboId")}
                className={`p-1 rounded text-[11px] mr-2 ${customerFilter.hasQboId === null
                  ? "bg-gray-300"
                  : customerFilter.hasQboId
                    ? "bg-green-300"
                    : "bg-red-300"
                  }`}
              >
                {customerFilter.hasQboId === null
                  ? tr("Any QBO ID")
                  : customerFilter.hasQboId
                    ? tr("Has QBO ID")
                    : "No QBO ID"}
              </button>
              <div className="relative">
                <button
                  className="p-2 rounded text-[11px] mr-2 bg-gray-300"
                  onClick={() =>
                    setShowCustomerSortPopup(!showCustomerSortPopup)
                  }
                >
                  <FaSortAlphaDown color="black" />
                </button>
                {showCustomerSortPopup && (
                  <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg border rounded">
                    {customerSortOptions.map((option) => (
                      <div key={option.value}>
                        <button
                          className="block w-full text-left p-2 hover:bg-gray-200"
                          onClick={() =>
                            handleSortCustomerChange(option.value, "ASC")
                          }
                        >
                          {tr(option.label)} (ASC)
                        </button>
                        <button
                          className="block w-full text-left p-2 hover:bg-gray-200"
                          onClick={() =>
                            handleSortCustomerChange(option.value, "DESC")
                          }
                        >
                          {tr(option.label)} (DESC)
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <input
              onChange={handleCustomerSearchChange}
              className="rounded-md border w-32 border-black"
            />
          </div>
          <div className="overflow-y-scroll flex-grow">
            {customers &&
              customers.map((customer: Customer) => (
                <CustomerCard
                  key={customer.id}
                  customer={customer}
                  invalidateQueries={invalidateQueries}
                />
              ))}
          </div>
        </div>
        <div className="border border-black rounded-md p-2 bg-slate-200 mb-2 pt-2 w-[32%] mr-1 flex flex-col">
          <div className="flex flex-row justify-between items-center mb-2">
            <h4 className="mr-2">{tr("Invoices")}</h4>
            <div className="flex flex-row items-center">
              <button
                onClick={() => handleInvoiceFilterChange("hasMessage")}
                className={`p-1 rounded text-[11px] mr-2 ${messageFilterClass(invoiceFilter.hasMessage)}`}
              >
                {messageFilterLabel(invoiceFilter.hasMessage)}
              </button>
              <button
                onClick={() => handleInvoiceFilterChange("hasQboId")}
                className={`p-1 rounded text-[11px] mr-2 ${invoiceFilter.hasQboId === null
                  ? "bg-gray-300"
                  : invoiceFilter.hasQboId
                    ? "bg-green-300"
                    : "bg-red-300"
                  }`}
              >
                {invoiceFilter.hasQboId === null
                  ? tr("Any QBO ID")
                  : invoiceFilter.hasQboId
                    ? tr("Has QBO ID")
                    : tr("No QBO ID")}
              </button>
              <div className="relative">
                <button
                  className="p-2 rounded text-[11px] mr-2 bg-gray-300"
                  onClick={() => setShowInvoiceSortPopup(!showInvoiceSortPopup)}
                >
                  <FaSortAlphaDown color="black" />
                </button>
                {showInvoiceSortPopup && (
                  <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg border rounded">
                    {invoiceSortOptions.map((option) => (
                      <div key={option.value}>
                        <button
                          className="block w-full text-left p-2 hover:bg-gray-200"
                          onClick={() =>
                            handleSortInvoiceChange(option.value, "ASC")
                          }
                        >
                          {tr(option.label)} (ASC)
                        </button>
                        <button
                          className="block w-full text-left p-2 hover:bg-gray-200"
                          onClick={() =>
                            handleSortInvoiceChange(option.value, "DESC")
                          }
                        >
                          {tr(option.label)} (DESC)
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <input
              onChange={handleInvoiceSearchChange}
              className="rounded-md border w-32 border-black"
            />
          </div>
          <div className="overflow-y-scroll flex-grow ">
            {invoices &&
              invoices.map((invoice: Invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  invalidateQueries={invalidateQueries}
                />
              ))}
          </div>
        </div>
        <div className="border border-black rounded-md p-2 bg-slate-200 mb-2 pt-2 w-[32%] mr-1 flex flex-col">
          <div className="flex flex-row justify-between items-center mb-2">
            <h4 className="mr-2">{tr("Payments")}</h4>
            <div className="flex flex-row items-center">
              <button
                onClick={() => handlePaymentFilterChange("hasMessage")}
                className={`p-1 rounded text-[11px] mr-2 ${messageFilterClass(paymentFilter.hasMessage)}`}
              >
                {messageFilterLabel(paymentFilter.hasMessage)}
              </button>
              <button
                onClick={() => handlePaymentFilterChange("hasQboId")}
                className={`p-1 rounded text-[11px] mr-2 ${paymentFilter.hasQboId === null
                  ? "bg-gray-300"
                  : paymentFilter.hasQboId
                    ? "bg-green-300"
                    : "bg-red-300"
                  }`}
              >
                {paymentFilter.hasQboId === null
                  ? tr("Any QBO ID")
                  : paymentFilter.hasQboId
                    ? tr("Has QBO ID")
                    : "No QBO ID"}
              </button>
              <div className="relative">
                <button
                  className="p-2 rounded text-[11px] mr-2 bg-gray-300"
                  onClick={() => setShowPaymentSortPopup(!showPaymentSortPopup)}
                >
                  <FaSortAlphaDown color="black" />
                </button>
                {showPaymentSortPopup && (
                  <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg border rounded">
                    {paymentSortOptions.map((option) => (
                      <div key={option.value}>
                        <button
                          className="block w-full text-left p-2 hover:bg-gray-200"
                          onClick={() =>
                            handleSortPaymentChange(option.value, "ASC")
                          }
                        >
                          {tr(option.label)} (ASC)
                        </button>
                        <button
                          className="block w-full text-left p-2 hover:bg-gray-200"
                          onClick={() =>
                            handleSortPaymentChange(option.value, "DESC")
                          }
                        >
                          {tr(option.label)} (DESC)
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <input
              onChange={handlePaymentSearchChange}
              className="rounded-md border w-32 border-black"
            />
          </div>
          <div className="overflow-y-scroll flex-grow">
            {payments &&
              payments.map((payment: any) => (
                <PaymentCard
                  key={payment.id}
                  payment={payment}
                  invalidateQueries={invalidateQueries}
                />
              ))}
          </div>
        </div>
      </div>
      {showClassMappingModal && (
        <ClassMappingModal onClose={() => setShowClassMappingModal(false)} />
      )}
      {showAccountMappingModal && (
        <AccountMappingModal onClose={() => setShowAccountMappingModal(false)} />
      )}
      {showOptionsModal && (
        <OptionsModal onClose={() => setShowOptionsModal(false)} />
      )}


    </div>
  );
};

export default QuickbooksOnline;
